import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A low-poly worker — the simple, reusable *actor* for interaction animations.
// (The richer game worker, with its profession tools and seated pose, is the
// separate `worker` model; this one is the bare rig the interaction format is
// built on.) Its parts are named for the actor rig (`body`, `head`, `arm-l`,
// `arm-r`, `leg-l`, `leg-r`), so an Interaction's `subject: "actor"` track can
// drive them; the whole figure also moves as one via the rig's `root` node.
// Modelled facing +Z (its forward), feet on the floor (y = 0), so a station's
// `facing` turns it to the model it engages and a forward lean is a positive
// rotation about X.
//
// It is also a normal catalogue model in its own right (it just stands there
// when nothing drives it): naming a part only makes it *posable*, it does not
// move on its own without an animation or interaction.
const SKIN = "#d8a06a";
const SHIRT = "#3f7cb0";
const PANTS = "#3a3f47";
const HAIR = "#3a2a1c";
const BOOT = "#241c14";

const LEG_H = 0.16;
const BODY_Y = LEG_H + 0.11;       // body centre, stacked on the legs
const SHOULDER_Y = BODY_Y + 0.07;  // arm hinge height
const HEAD_Y = BODY_Y + 0.12 + 0.06;

function leg(name: string, x: number): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.07, LEG_H, 0.08), color: PANTS, position: [x, LEG_H / 2, 0], name },
    { geometry: new THREE.BoxGeometry(0.075, 0.04, 0.11), color: BOOT, position: [x, 0.02, 0.015], name },
  ];
}

// An arm hung at the shoulder. Authored straight down; an interaction rotates it
// about the shoulder pivot (see well.ts).
function arm(name: string, x: number): Part {
  return { geometry: new THREE.BoxGeometry(0.045, 0.18, 0.05), color: SHIRT, position: [x, SHOULDER_Y - 0.07, 0], name };
}

export const workerSimple = defineModel({
  id: "worker-simple",
  batchId: "characters",
  name: "Worker (simple)",
  tags: ["character", "actor", "humanoid", "person", "rig"],
  summary: "A low-poly rig actor — the simple worker that interaction animations pose.",
  description:
    "The standard actor for the interaction format. Its parts carry the actor-rig " +
    "names so any Interaction can pose it at a station; the whole figure also moves " +
    "as one via the rig's `root` node.",
  author: "models",
  footprint: 0.12,
  parts(): Part[] {
    return [
      ...leg("leg-l", -0.05),
      ...leg("leg-r", 0.05),
      // Torso.
      { geometry: new THREE.BoxGeometry(0.18, 0.22, 0.11), color: SHIRT, position: [0, BODY_Y, 0], name: "body" },
      // A small hip block so the waist reads where shirt meets trousers.
      { geometry: new THREE.BoxGeometry(0.18, 0.05, 0.11), color: PANTS, position: [0, LEG_H + 0.025, 0], name: "body" },
      arm("arm-l", -0.115),
      arm("arm-r", 0.115),
      // Head + hair.
      { geometry: new THREE.BoxGeometry(0.12, 0.12, 0.12), color: SKIN, position: [0, HEAD_Y, 0], name: "head" },
      { geometry: new THREE.BoxGeometry(0.13, 0.04, 0.13), color: HAIR, position: [0, HEAD_Y + 0.06, 0], name: "head" },
    ];
  },
  animations: [],
});
